import React, { useState, useEffect, useRef } from 'react'
import { useTranslate, useNotify, useRefresh } from 'react-admin'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  CircularProgress,
  Grid,
  MenuItem,
  Skeleton,
} from '@mui/material'
import SaveIcon from '@mui/icons-material/Save'
import { LoadingButton } from '@mui/lab'
import { EditInputsWrap } from 'src/components/uiElements/CRUD/EditInputsWrap'
import { supplierServices } from 'src/services/reservation/supplier/supplier.service'
import { itemSupplierService } from 'src/services/reservation/itemSupplier/itemSupplier.service'
import { currencyServices } from 'src/services/reservation/currency/currency.service'

export const CreateItemSupplierModal = ({ open, itemId, onClose }) => {
  const translate = useTranslate()
  const notify = useNotify()
  const refresh = useRefresh()

  const [loading, setLoading] = useState(false)
  const [supplierId, setSupplierId] = useState(null)
  const [supplierOptions, setSupplierOptions] = useState([])
  const [supplierLoading, setSupplierLoading] = useState(false)
  const [amountTotal, setAmountTotal] = useState('')
  const [dueDate, setDueDate] = useState(new Date().toISOString().split('T')[0])
  const [currencyId, setCurrencyId] = useState(null)
  const [code, setCode] = useState('')
  const [currencyList, setCurrencyList] = useState([])
  const [loadingCurrencyList, setLoadingCurrencyList] = useState(false)

  const searchTimeoutRef = useRef(null)

  useEffect(() => {
    if (open) {
      setSupplierId(null)
      setSupplierOptions([])
      setAmountTotal('')
      setDueDate(new Date().toISOString().split('T')[0])
      setCurrencyId(null)
      setCode('')
      getCurrencies()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const getCurrencies = async () => {
    try {
      setLoadingCurrencyList(true)
      const allCurrencies = await currencyServices.getByAll()
      setCurrencyList(allCurrencies)
      setLoadingCurrencyList(false)
    } catch (error) {
      setCurrencyList([])
      setLoadingCurrencyList(false)
    }
  }

  const getSupplier = (name) => {
    clearTimeout(searchTimeoutRef.current)
    let params = {}
    if (Number.isNaN(Number(name))) {
      params.name = name
    } else {
      params.id = Number(name)
    }
    searchTimeoutRef.current = setTimeout(async () => {
      try {
        setSupplierLoading(true)
        const results = await supplierServices.getByParam(params)
        setSupplierOptions(results)
        setSupplierLoading(false)
      } catch (error) {
        setSupplierOptions([])
        setSupplierLoading(false)
      }
    }, 700)
  }

  const handleSubmit = async () => {
    if (loading) return
    setLoading(true)
    try {
      await itemSupplierService.createItemSecondarySupplier({
        item_id: itemId,
        supplier_id: supplierId,
        amount_total: Number(amountTotal),
        amount_pending: Number(amountTotal),
        due_date: dueDate,
        status: 'UNPAID',
        currency_id: currencyId,
        code,
      })
      notify('ra.notification.create_item_supplier_success', { type: 'success' })
      onClose()
      refresh()
    } catch (error) {
      notify('ra.notification.create_item_supplier_error', { type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const selectedSupplier = supplierOptions.find((s) => s.id === supplierId) || null

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle>{translate('ra.title.create_item_supplier')}</DialogTitle>
      <DialogContent>
        <Grid container spacing={3} sx={{ mt: 1 }}>
          <EditInputsWrap label={translate('ra.field.supplier')} required md={12}>
            <Autocomplete
              options={supplierOptions}
              getOptionLabel={(option) => `${option.id} - ${option.name}`}
              isOptionEqualToValue={(option, value) => option.id === value?.id}
              loading={supplierLoading}
              value={selectedSupplier}
              onChange={(e, newValue) => setSupplierId(newValue?.id ?? null)}
              onInputChange={(e, newInputValue) => {
                if (e?.type === 'change' || !e) {
                  getSupplier(newInputValue)
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size='small'
                  fullWidth
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {supplierLoading ? <CircularProgress color='inherit' size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </EditInputsWrap>

          <EditInputsWrap label={translate('ra.field.currency')} required md={6}>
            {loadingCurrencyList ? (
              <Skeleton variant='rectangular' width={'100%'} height={40} />
            ) : (
              <TextField
                select
                size='small'
                fullWidth
                value={currencyId ?? ''}
                onChange={(e) => setCurrencyId(e.target.value)}
              >
                {currencyList.map((currency) => (
                  <MenuItem key={currency.id} value={currency.id}>
                    {`${currency.symbol} ${currency.long_name} (${currency.short_name})`}
                  </MenuItem>
                ))}
              </TextField>
            )}
          </EditInputsWrap>

          <EditInputsWrap label={translate('ra.field.amount')} required md={6}>
            <TextField
              type='number'
              step='any'
              size='small'
              fullWidth
              value={amountTotal}
              onChange={(e) => setAmountTotal(e.target.value)}
            />
          </EditInputsWrap>

          <EditInputsWrap label={translate('ra.field.due_date')} required md={6}>
            <TextField
              type='date'
              size='small'
              fullWidth
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
            />
          </EditInputsWrap>
          
          <EditInputsWrap label={translate('ra.field.code')} md={6}>
            <TextField
              size='small'
              fullWidth
              value={code}
              inputProps={{ maxLength: 255 }}
              onChange={(e) => setCode(e.target.value)}
            />
          </EditInputsWrap>

        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} variant='outlined' color='error' disabled={loading}>
          {translate('ra.action.cancel')}
        </Button>
        <LoadingButton
          loading={loading}
          variant='contained'
          startIcon={<SaveIcon />}
          onClick={handleSubmit}
          disabled={!supplierId || !amountTotal || !dueDate}
        >
          {translate('ra.action.save')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}
